/**
 * studentAuthStore.ts  -  Zustand store for student session state.
 * Persisted to sessionStorage (clears on tab close).
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export interface StudentClass {
  id: string;
  name: string;
  teacherName: string;
  liveSession: { id: string; title: string | null; startedAt: string } | null;
}

export interface StudentUser {
  id: string;
  displayName: string;
  username: string;
  avatarConfig: Record<string, unknown>;
  ageGroup: string;
  totalXp: number;
  coins: number;
  streak: number;
  classes: StudentClass[];
  badges: {
    slug: string;
    name: string;
    iconKey: string | null;
    earnedAt: string;
  }[];
}

interface StudentAuthState {
  token: string | null;
  student: StudentUser | null;
  isLoading: boolean;

  setStudent: (token: string, student: StudentUser) => void;
  clearStudent: () => void;
  setLoading: (loading: boolean) => void;
  updateStudent: (partial: Partial<StudentUser>) => void;
}

export const useStudentAuthStore = create<StudentAuthState>()(
  persist(
    (set) => ({
      token: null,
      student: null,
      isLoading: true,

      setStudent: (token, student) => set({ token, student, isLoading: false }),
      clearStudent: () => set({ token: null, student: null, isLoading: false }),
      setLoading: (loading) => set({ isLoading: loading }),
      updateStudent: (partial) =>
        set((s) => ({
          student: s.student ? { ...s.student, ...partial } : null,
        })),
    }),
    {
      name: "pq-student-auth",
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        // After localStorage hydration (sync or async), mark loading done.
        // Without this, isLoading stays true forever on a fresh origin (e.g. first HTTPS visit).
        if (state) state.isLoading = false;
      },
    },
  ),
);
